import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  onMounted,
  inject,
  computed,
  onBeforeUnmount,
  PropType,
} from "vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import BookCalendar from "@/components/book/BookCalendar.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  ShowErrorModal,
  BookIntervalCode,
} from "@/types";
import { useBookStore } from "@/stores/bookStore";
import { useLineStore } from "@/stores/lineStore";
import {
  IBookableDatesInfo,
  IDateInfo,
  ISeatInfo,
} from "@/domain/entities/book";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import EmptyContent from "@/components/shared/EmptyContent.vue";
import { IBookInterval, ILineFare } from "@/domain/entities/lineInfo";
import SeatAndPayment from "@/components/book/SeatAndPayment.vue";


const __default__ = {
  name: "BookDetail",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  turnId: {
    type: String,
    required: true,
  },
  getOnBusStopId: {
    type: String,
    required: true,
  },
  getOffBusStopId: {
    type: String,
    required: true,
  },
  lineFare: {
    type: Object as PropType<ILineFare>,
    required: true,
  },
  bookIntervals: {
    type: Array as PropType<IBookInterval[]>,
    required: true,
  },
  isMonthlyBook: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onClickClose"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const lineStore = useLineStore();
const bookStore = useBookStore();

const calendarWrapper = ref<HTMLElement>();
const bookingDates = ref<Array<IDateInfo>>([]); // 예약 진행중인 날짜

const lineInfo = lineStore.getLineInfoByTurnId(props.turnId);
const turnInfo = lineStore.getTurnByTurnId(props.turnId);
const bookableDatesInfos = ref<IBookableDatesInfo[]>([]);
const duplicateSeatNo = ref<string | undefined>(undefined);

const selectedDateInfo = ref<IDateInfo>();
const selectedDayElement = ref<HTMLElement | undefined>();

const oneTimeFare = computed(() => {
  return currentBookInterval.value === BookIntervalCode.MONTH
    ? props.lineFare?.monthlyFare
    : props.lineFare?.dailyFare;
});

const allowWaitList = computed(() => {
  return false;
  // ToDo : return lineInfo.allowWaitList;
});

const currentBookInterval = ref(
  props.isMonthlyBook ? BookIntervalCode.MONTH : BookIntervalCode.DAY
);

const seatsSelectable = computed(() => {
  const currentBookIntervalInfo = props.bookIntervals.find(
    (bookInterval) =>
      bookInterval.bookIntervalCode === currentBookInterval.value
  );
  return !!currentBookIntervalInfo?.seatsSelectable;
});

const getTitle = () => {
  if (!turnInfo || !lineInfo) return "예약하기";

  const lineName = useGetLineRouteName(
    lineInfo.lineTypeCode,
    lineInfo.routeName,
    lineInfo.lineNum
  );
  return `[${turnInfo.firstStop.departureTime}] ${lineName}`;
};

/** 예약가능한 날짜 조회하기 */
const updateKey = ref(1);
const getBookableDates = async () => {
  const bookableDatesResult = await bookStore.getBookableDates(
    props.turnId,
    props.getOnBusStopId,
    props.getOffBusStopId,
    currentBookInterval.value,
    props.lineFare
  );

  if (!bookableDatesResult.isSuccess) {
    showErrorModal(
      "예약일자 오류",
      bookableDatesResult.errorMessage,
      bookableDatesResult.resultCode,
      bookableDatesResult.errorTime
    );
    return;
  }
  bookableDatesInfos.value = bookableDatesResult.data ?? [];
};

/** 좌석 선택시 로직 처리 */
const onClickSeat = (seatNo: string) => {
  const dateInfo = selectedDateInfo.value!;
  dateInfo.bookableDate!.mySeatNo = seatNo;
  duplicateSeatNo.value = undefined;
};

const getBaseSeatNo = () => {
  return bookingDates.value[0]?.bookableDate?.mySeatNo;
};

const updateSeatNo = (
  targetDateInfo: IDateInfo,
  seatsInfosList: (ISeatInfo | undefined)[][]
) => {
  const baseSeatNo = getBaseSeatNo();
  if (!baseSeatNo) return;

  const targetDaySeatInfo = seatsInfosList
    .flat()
    .find((seatInfo) => seatInfo?.seatNo === baseSeatNo);

  if (!targetDaySeatInfo) return;

  if (targetDaySeatInfo?.bookedBySomeone) {
    duplicateSeatNo.value = baseSeatNo;
    return;
  }

  targetDateInfo.bookableDate!.mySeatNo = baseSeatNo;
};

const removeSeatNo = (targetDateInfo: IDateInfo) => {
  if (!targetDateInfo.bookableDate?.mySeatNo) return;
  targetDateInfo.bookableDate.mySeatNo = undefined;
};

/** 날짜 클릭시 로직 처리 */
const hasBookingDate = (targetDateInfo: IDateInfo) => {
  return bookingDates.value.some(
    (dateInfo) => dateInfo.date === targetDateInfo.date
  );
};

/** 예약 대기 처리 */
const showWaitingConfirm = ref(false);
const toggleShowWaitingConfirmModal = (show: boolean) => {
  showWaitingConfirm.value = show;
};
const registerWaitingList = () => {
  toggleShowWaitingConfirmModal(false);
  // ToDo: 예약대기 신청 후 bookableDates 갱신
};

const onClickDay = async (
  targetDateInfo: IDateInfo,
  target: EventTarget | null
) => {
  resetSelectedDateInfo();
  if (hasBookingDate(targetDateInfo)) {
    cancelBookProcess(targetDateInfo);
    return;
  }
  proceedWithBookProcess(targetDateInfo, target);
};

const proceedWithBookProcess = async (
  targetDateInfo: IDateInfo,
  target: EventTarget | null
) => {
  selectedDayElement.value = target as HTMLElement;
  if (!bookingDates.value.length) {
    scrollCalendarIfNeeded();
  }
  updateBookingDate(targetDateInfo);
};

const cancelBookProcess = async (targetDateInfo: IDateInfo) => {
  removeBookingDate(targetDateInfo);
  removeSeatNo(targetDateInfo);
};

const resetSelectedDateInfo = () => {
  selectedDateInfo.value = undefined;
  selectedDayElement.value = undefined;
};

const updateBookingDate = (targetDateInfo: IDateInfo) => {
  if (hasBookingDate(targetDateInfo)) return;

  const isDailyBookWithSeats =
    currentBookInterval.value === BookIntervalCode.DAY && seatsSelectable.value; // 일예약 지정석인지 여부
  if (isDailyBookWithSeats) {
    removeBookingDatesWithNoSeats();
  }

  bookingDates.value = [...bookingDates.value, targetDateInfo];
  selectedDateInfo.value = targetDateInfo;
};

const removeBookingDatesWithNoSeats = () => {
  duplicateSeatNo.value = undefined;
  bookingDates.value = bookingDates.value.filter(
    (bookingDate) => !!bookingDate.bookableDate?.mySeatNo
  );
};

const removeBookingDate = (targetDateInfo: IDateInfo) => {
  bookingDates.value = bookingDates.value.filter(
    (bookingDate) => bookingDate.date !== targetDateInfo.date
  );
  selectedDateInfo.value = bookingDates.value[bookingDates.value.length - 1];
};

const updateFooterWrapperHeight = (footerHeight: number) => {
  if (!footerHeight) return;

  document.documentElement.style.setProperty(
    "--footer-height",
    `${footerHeight}px`
  );
  scrollCalendarIfNeeded();
};

const scrollCalendarIfNeeded = () => {
  if (!selectedDayElement.value) {
    return;
  }
  selectedDayElement.value.scrollIntoView({
    block: "center",
    behavior: "smooth",
  });
};

const totalAmount = computed(() => {
  if (oneTimeFare.value === undefined) return undefined;

  return currentBookInterval.value === BookIntervalCode.MONTH
    ? oneTimeFare.value
    : (bookingDates.value?.length ?? 0) * oneTimeFare.value;
});

onMounted(async () => {
  await getBookableDates();
});

onBeforeUnmount(() => {
  document.documentElement.style.setProperty("--footer-height", "0px");
});

const __returned__ = { props, emit, showErrorModal, lineStore, bookStore, calendarWrapper, bookingDates, lineInfo, turnInfo, bookableDatesInfos, duplicateSeatNo, selectedDateInfo, selectedDayElement, oneTimeFare, allowWaitList, currentBookInterval, seatsSelectable, getTitle, updateKey, getBookableDates, onClickSeat, getBaseSeatNo, updateSeatNo, removeSeatNo, hasBookingDate, showWaitingConfirm, toggleShowWaitingConfirmModal, registerWaitingList, onClickDay, proceedWithBookProcess, cancelBookProcess, resetSelectedDateInfo, updateBookingDate, removeBookingDatesWithNoSeats, removeBookingDate, updateFooterWrapperHeight, scrollCalendarIfNeeded, totalAmount, NavigationBar, BookCalendar, Modal, EmptyContent, SeatAndPayment }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})